import React from 'react';
import { ChevronRight, BookOpen, TrendingUp, AlertTriangle, ShieldCheck } from 'lucide-react';
import { createRiskSummary } from '../utils/riskInterpreter';


// Helper to render markdown-style bold **text**
const JUNK_VALUES = new Set(['not specified', 'not provided', 'not given', 'unknown', 'none']);

const cleanPipeString = (text) => {
  if (!text) return text;
  return text
    .split('|')
    .map(p => p.trim())
    .filter(p => p && !JUNK_VALUES.has(p.toLowerCase()))
    .join(' | ');
};

const renderMarkdown = (text) => {
  if (!text) return text;
  const cleaned = cleanPipeString(text);
  if (!cleaned) return null;
  const parts = cleaned.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const SNPCard = ({ snp, onClick }) => {
  const riskSummary = createRiskSummary(snp);
  const hasGWAS = snp.gwas?.traits?.length > 0;

  const getCategoryStyle = (category) => {
    const styles = {
      'pathogenic':         'bg-rose-50    border-rose-300    text-rose-900',
      'likely-pathogenic':  'bg-orange-50  border-orange-300  text-orange-900',
      'risk':               'bg-amber-50   border-amber-300   text-amber-900',
      'protective':         'bg-emerald-50 border-emerald-300 text-emerald-900',
      'drug-response':      'bg-violet-50  border-violet-300  text-violet-900',
      'uncertain':          'bg-sky-50     border-sky-300     text-sky-900',
      'likely-inactive':    'bg-slate-50   border-slate-200   text-slate-500',
      'unknown':            'bg-slate-50   border-slate-300   text-slate-900',
    };
    return styles[category] || styles.unknown;
  };

  // Find most relevant GWAS hit (where user carries the risk allele)
  const carriedAssoc = snp.gwas?.topAssociations?.find(a => a.copiesCarried > 0);
  const topGWASHit = carriedAssoc || snp.gwas?.topAssociations?.[0];

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg px-4 py-3.5 border border-slate-200 hover:border-indigo-400 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/0 via-indigo-50/50 to-indigo-50/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

      <div className="relative flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">

          {snp.reviewStatus && (
            <p className="text-xs text-sky-600 mb-1.5 font-medium">{snp.reviewStatus}</p>
          )}

          {/* Header row */}
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <span className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
              {snp.rsid}
            </span>
            <span className="text-sm font-medium text-slate-500">{snp.gene}</span>
            <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-mono font-semibold text-slate-600">
              {snp.genotype}
            </span>
            <span className="text-base">{riskSummary.icon}</span>
            <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${getCategoryStyle(riskSummary.category)}`}>
              {riskSummary.riskLevel}
            </span>
            {hasGWAS && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded-md text-xs font-medium text-violet-700">
                <TrendingUp className="w-2.5 h-2.5" />
                GWAS
              </span>
            )}
          </div>

          {/* Primary message (now GWAS-aware from riskInterpreter) */}
          {riskSummary.primaryMessage && (
            <p className="text-sm text-slate-700 leading-relaxed">{renderMarkdown(riskSummary.primaryMessage)}</p>
          )}

          {/* Additional details */}
          {riskSummary.details.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {riskSummary.details.slice(0, 2).map((detail, idx) => {
                const parts = detail.split('|').map(p => p.trim()).filter(p => p && !JUNK_VALUES.has(p.toLowerCase()));
                if (parts.length <= 1) {
                  return (
                    <p key={idx} className="text-xs text-slate-500 leading-relaxed">
                      <span className="text-indigo-400 mr-1">→</span>{renderMarkdown(detail)}
                    </p>
                  );
                }
                const preview = parts.slice(0, 3).join(', ');
                const extra = parts.length - 3;
                return (
                  <p key={idx} className="text-xs text-slate-500 leading-relaxed">
                    <span className="text-indigo-400 mr-1">→</span>
                    {renderMarkdown(preview)}{extra > 0 ? ', +' + extra + ' more' : ''}
                  </p>
                );
              })}
            </div>
          )}

          {/* GWAS trait pills (compact, bottom) */}
          {hasGWAS && snp.gwas.traits.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 mt-3">
              <BookOpen className="w-3 h-3 text-violet-500 flex-shrink-0" />
              {snp.gwas.traits.slice(0, 3).map((trait, idx) => (
                <span key={idx} className="px-1.5 py-0.5 bg-violet-50 text-violet-700 text-xs rounded border border-violet-100">
                  {trait}
                </span>
              ))}
              {snp.gwas.traits.length > 3 && (
                <span className="text-xs text-violet-500">+{snp.gwas.traits.length - 3}</span>
              )}
            </div>
          )}
        </div>

        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors flex-shrink-0 mt-0.5" />
      </div>
    </div>
  );
};

export default SNPCard;